import { useTranslation } from 'module/hooks/use-translation';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
  DefaultCustomLayoutDrawerProps,
} from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { Form } from 'module/ui/components/atoms/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Input } from 'module/ui/components/atoms/input/input';
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { Col, InputRef, Row } from 'antd';
import { BlacklistedDomain } from 'queries/emails';
import {
  useCreateBlacklistedDomain,
  useUpdateBlacklistedDomain,
} from 'queries/emails';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface Props {
  domain?: BlacklistedDomain;
  visible: boolean;
  onClose: VoidFunction;
}

export const ManageDomainsDrawer: FC<Props> = ({
  visible,
  onClose,
  domain,
}) => {
  const isEdit = domain != null;
  const requiredMessage = useTranslation('input.required');
  const [form] = Form.useForm<{ domain: string }>();
  const [domainState, setDomainState] = useState<{
    old: string;
    updated: string;
  }>({ old: '', updated: '' });

  const { mutateAsync: updateDomainApi, isPending: isUpdateFieldPending } =
    useUpdateBlacklistedDomain();
  const { mutateAsync: createDomainApi, isPending: isCreateFieldPending } =
    useCreateBlacklistedDomain();
  const title = useTranslation(
    `settings.domain_blacklist.${isEdit ? 'edit' : 'create'}_drawer.title`,
  );

  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (visible) {
      inputRef.current?.focus();
    }
    if (isEdit) {
      const domainValue = domain.email;

      form.setFieldsValue({
        domain: domainValue,
      });
      setDomainState({ old: domainValue, updated: domainValue });
    } else {
      form.setFieldsValue({
        domain: '',
      });
      setDomainState({ old: '', updated: '' });
    }
  }, [domain, form, isEdit, visible]);
  const onSave = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        if (isEdit) {
          updateDomainApi({
            oldEmail: domainState.old,
            newEmail: values.domain,
          })
            .then(() => onClose())
            .catch(() => {});
        } else {
          createDomainApi({
            email: values.domain,
          })
            .then(() => onClose())
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [
    createDomainApi,
    form,
    isEdit,
    onClose,
    updateDomainApi,
    domainState.old,
  ]);

  return (
    <CustomLayoutDrawer
      open={visible}
      onClose={onClose}
      width={664}
      {...DefaultCustomLayoutDrawerProps}
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title>
          {title}
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          <Form layout="vertical" form={form}>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="domain"
                  label={
                    <FormattedMessage id="settings.domain_blacklist.domain_field" />
                  }
                  required
                  rules={[{ required: true, message: requiredMessage }]}
                >
                  <Input
                    value={domain?.email}
                    onChange={(e) => {
                      const newValue = e.target.value;

                      form.setFieldsValue({
                        domain: newValue,
                      });
                      setDomainState((prev) => ({
                        ...prev,
                        updated: newValue,
                      }));
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer $rightAlignedAction>
          <Button onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            variant="primary"
            leftIcon={<FontAwesomeIcon icon={faSave} />}
            onClick={onSave}
            loading={isUpdateFieldPending || isCreateFieldPending}
          ></Button>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
